import { PDA_DEVICE_CODE_PATTERN, PDA_TEMPORARY_REFERENCE_PATTERN } from '@fuel-system/contracts';

/** RFC 4648 base32 alphabet: uppercase letters and 2-7, no 0/1/8/9 to misread. */
const BASE32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
export const DEVICE_CODE_LENGTH = 6;

/** Random bytes from WebCrypto (available outside a secure context too). */
export function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

/**
 * The device code of fuel-pda.md 13.5: 6 uppercase base32 characters, made
 * once per install. Each byte picks one character (256 is a multiple of 32,
 * so the choice is uniform).
 */
export function generateDeviceCode(bytes: Uint8Array = randomBytes(DEVICE_CODE_LENGTH)): string {
  if (bytes.length < DEVICE_CODE_LENGTH) throw new RangeError('Not enough random bytes');
  let code = '';
  for (let i = 0; i < DEVICE_CODE_LENGTH; i += 1) code += BASE32[(bytes[i] ?? 0) % 32];
  return code;
}

/** `PD-{deviceCode}-{seq}`, the reference an offline purchase prints until it is synced. */
export function formatTemporaryReference(deviceCode: string, seq: number): string {
  if (!PDA_DEVICE_CODE_PATTERN.test(deviceCode)) {
    throw new RangeError(`Not a device code: ${deviceCode}`);
  }
  if (!Number.isInteger(seq) || seq < 1) throw new RangeError(`Not a sequence number: ${seq}`);
  return `PD-${deviceCode}-${seq}`;
}

export function isTemporaryReference(value: string): boolean {
  return PDA_TEMPORARY_REFERENCE_PATTERN.test(value.trim().toUpperCase());
}

/** A uuid for an install or an operation; `crypto.randomUUID` needs a secure context. */
export function newUuid(): string {
  if (typeof globalThis.crypto.randomUUID === 'function') return globalThis.crypto.randomUUID();
  const bytes = randomBytes(16);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
